import axios from 'axios';
import { getApiBaseURL } from '../utils/apiUrl';

const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 30000,
});

let refreshPromise = null;

function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
}

async function refreshAccessToken() {
  const rt = localStorage.getItem('refreshToken');
  if (!rt) return null;
  const base = getApiBaseURL();
  try {
    const { data } = await axios.post(
      `${base}/api/auth/refresh`,
      { refreshToken: rt },
      { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
    );
    if (data?.token) localStorage.setItem('token', data.token);
    if (data?.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    return data?.token || null;
  } catch {
    return null;
  }
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    console.error('API Error:', response?.data || error.message);

    if (response?.status === 403 && response.data?.error === 'ACCOUNT_PENDING_APPROVAL') {
      clearSession();
      window.location.reload();
      return Promise.reject(error);
    }

    if (!response || response.status !== 401 || !config) {
      return Promise.reject(error);
    }

    const path = String(config.url || '');
    const authPaths =
      path.includes('/api/auth/refresh') ||
      path.includes('/api/login') ||
      path.includes('/api/login/2fa');

    const errCode = response.data?.error;

    if (errCode !== 'TokenExpired') {
      if (!authPaths) {
        return Promise.reject(error);
      }
      clearSession();
      window.location.reload();
      return Promise.reject(error);
    }

    if (authPaths || !localStorage.getItem('refreshToken')) {
      clearSession();
      window.location.reload();
      return Promise.reject(error);
    }

    if (config._retryAfterRefresh) {
      clearSession();
      window.location.reload();
      return Promise.reject(error);
    }

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (!newToken) {
        clearSession();
        window.location.reload();
        return Promise.reject(error);
      }
      config._retryAfterRefresh = true;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newToken}`;
      return api.request(config);
    } catch {
      clearSession();
      window.location.reload();
      return Promise.reject(error);
    }
  }
);

export default api;
